import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ClientError } from '@chainlit/react-client';

import { useTheme } from '@/components/ThemeProvider'; // RAGBOT
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

import Alert from './Alert';
import { ProviderButton } from './ProviderButton';
import ArCaptcha from './Captcha'; // RAGBOT
import { rateLimitApiClient } from './RateLimitApi'; // RAGBOT

interface Props {
  error?: string;
  providers: string[];
  callbackUrl: string;
  onPasswordSignIn?: (
    email: string,
    password: string,
    callbackUrl: string
  ) => Promise<any>;
  onOAuthSignIn?: (provider: string, callbackUrl: string) => Promise<any>;
}

interface FormValues {
  email: string;
  password: string;
}

export function LoginForm({
  providers,
  onPasswordSignIn,
  onOAuthSignIn,
  callbackUrl,
  error
}: Props) {
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(error);
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const CAPTCHA_SITE_KEY = 'uh5ysrcdnz'; // RAGBOT: Captcha when ratelimited - TODO: import.meta.env.VITE_CAPTCHA_SITE_KEY;
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false); // RAGBOT
  const [captchaToken, setCaptchaToken] = useState<string | undefined>( // RAGBOT
    undefined
  );
  const [captchaKey, setCaptchaKey] = useState(0); // RAGBOT: for resetting captcha

  const { variant: theme } = useTheme(); // RAGBOT

  useEffect(() => {
    setErrorState(error);
  }, [error]);

  // RAGBOT
  const containsErrorMessage = (error: any, messages: string[]) => {
    return messages.some((message) => {
      return (
        error?.message?.toLowerCase().includes(message.toLowerCase()) ||
        error?.toString().toLowerCase().includes(message.toLowerCase()) ||
        error?.detail?.toLowerCase().includes(message.toLowerCase())
      );
    });
  };

  const onSubmit = async (data: FormValues) => { 
    try { // RAGBOT
      if (!onPasswordSignIn) return;

      const status = await rateLimitApiClient.getRateLimitStatus(data.email);
      setIsCaptchaRequired(status == 'captcha_required');

      if (isCaptchaRequired && !captchaToken) {
        setErrorState('captcharequired');
        return;
      }

      setLoading(true);

      if (isCaptchaRequired) {
        await rateLimitApiClient.verifyCaptcha(data.email, captchaToken!);
        setCaptchaKey((k) => k + 1); // reset captcha after submit
        setCaptchaToken(undefined);
      }
      await onPasswordSignIn(data.email, data.password, callbackUrl);
    } catch (err: any) {
      if (err instanceof ClientError && err.detail) {
        setErrorState(err.detail);
      } else if (err?.status === 429) { // RAGBOT
        setErrorState('blocked');
      } else if (err instanceof Error || err instanceof ClientError) { // RAGBOT
        if (containsErrorMessage(err, ['Too Many Requests']))
          setErrorState('blocked');
        else if (
          containsErrorMessage(err, [
            'Unauthorized',
            'credentialssignin',
            'invalid_credentials',
            'Error logging in'
          ])
        )
          setErrorState('credentialssignin');
        else setErrorState(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const oAuthReady = onOAuthSignIn && providers.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6')}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          <Translator path="auth.login.title" />
        </h1>
      </div>

      {errorState && (
        <Alert variant="error">
          {t([
            `auth.login.errors.${errorState}`,
            `auth.login.errors.${errorState.toLowerCase()}`,
            `auth.login.errors.default`
          ])}
        </Alert>
      )}

      <div className="grid gap-6">
        {onPasswordSignIn && (
          <>
            <div className="grid gap-2">
              <Label htmlFor="email">
                <Translator path="auth.login.form.email.label" />
              </Label>
              <Input
                id="email"
                disabled={loading}
                autoFocus
                placeholder={t('auth.login.form.email.placeholder')}
                {...register('email', {
                  required: t('auth.login.form.email.required')
                })}
                className={cn(
                  touchedFields.email && errors.email && 'border-destructive'
                )}
              />
              {touchedFields.email && errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  <Translator path="auth.login.form.password.label" />
                </Label>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  disabled={loading}
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: t('auth.login.form.password.required')
                  })}
                  className={cn(
                    touchedFields.password &&
                      errors.password &&
                      'border-destructive'
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2" // RAGBOT: reveal button on the left (RTL layout)
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {touchedFields.password && errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            {isCaptchaRequired && ( // RAGBOT: Captcha component
              <ArCaptcha
                key={captchaKey}
                siteKey={CAPTCHA_SITE_KEY}
                theme={theme}
                onSubmit={setCaptchaToken}
              />
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              <Translator path="auth.login.form.actions.signin" />
            </Button>
          </>
        )}

        {onPasswordSignIn && oAuthReady ? (
          <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
            <span className="relative z-10 bg-background px-2 text-muted-foreground">
              <Translator path="auth.login.form.alternativeText.or" />
            </span>
          </div>
        ) : null}

        {oAuthReady ? (
          <div className="grid gap-2">
            {providers.map((provider, index) => (
              <ProviderButton
                key={`provider-${index}`}
                provider={provider}
                onClick={() => onOAuthSignIn?.(provider, callbackUrl)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </form>
  );
}
